// src/lib/snapsite-colors.ts
// Paleta unificada de colores para el sistema de personalización de SnapSite.
// Las keys coinciden con los identifiers del sistema anterior (theme) para migración automática.

export interface AccentColor {
  key: string;
  label: string;
  primary: string;            // HSL triplet para --primary
  primaryForeground: string;  // HSL triplet para --primary-foreground
  previewHex: string;         // Color hex aproximado para el chip de preview
}

export interface PrimaryColor {
  key: string;
  label: string;
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  muted: string;
  mutedForeground: string;
  border: string;
  input: string;
  secondary: string;
  secondaryForeground: string;
  previewHex: string;   // Color hex del background para el chip
  isDark: boolean;      // true si el fondo es oscuro (para color del texto del chip)
}

// ── Accent colors ────────────────────────────────────────────────────────────
// Extraídos del campo --primary de cada archivo CSS de tema

export const ACCENT_COLORS: Record<string, AccentColor> = {
  default: {
    key: 'default',
    label: 'Melocotón',
    primary: '30 62% 72%',
    primaryForeground: '25 50% 10%',
    previewHex: '#E8A876',
  },
  'gris-oscuro': {
    key: 'gris-oscuro',
    label: 'Rojo',
    primary: '0 100% 50%',
    primaryForeground: '0 0% 100%',
    previewHex: '#FF0000',
  },
  'azul-petroleo': {
    key: 'azul-petroleo',
    label: 'Ámbar',
    primary: '30 80% 65%',
    primaryForeground: '30 80% 10%',
    previewHex: '#F0932A',
  },
  'verde-corporate': {
    key: 'verde-corporate',
    label: 'Verde Lima',
    primary: '90 50% 75%',
    primaryForeground: '90 50% 15%',
    previewHex: '#BFDF9F',
  },
  'rosa-suave': {
    key: 'rosa-suave',
    label: 'Rosa',
    primary: '320 60% 65%',
    primaryForeground: '320 60% 98%',
    previewHex: '#D875B4',
  },
  'ambar': {
    key: 'ambar',
    label: 'Naranja',
    primary: '30 90% 55%',
    primaryForeground: '30 90% 98%',
    previewHex: '#E88210',
  },
  'morado-lavanda': {
    key: 'morado-lavanda',
    label: 'Morado',
    primary: '275 50% 60%',
    primaryForeground: '275 50% 98%',
    previewHex: '#9966CC',
  },
  'azul-suave': {
    key: 'azul-suave',
    label: 'Azul',
    primary: '215 70% 60%',
    primaryForeground: '215 70% 98%',
    previewHex: '#528DE0',
  },
  'azul-electrico': {
    key: 'azul-electrico',
    label: 'Azul Eléctrico',
    primary: '210 100% 62%',
    primaryForeground: '150 60% 4%',
    previewHex: '#3D9EFF',
  },
  'terminal-70s': {
    key: 'terminal-70s',
    label: 'Verde Terminal',
    primary: '150 70% 50%',
    primaryForeground: '150 60% 4%',
    previewHex: '#22C55E',
  },
};

// ── Primary / background colors ───────────────────────────────────────────────
// Extraídos de los campos --background, --card, --foreground, etc. de cada CSS

export const PRIMARY_COLORS: Record<string, PrimaryColor> = {
  default: {
    key: 'default',
    label: 'Claro',
    background: '240 14% 95%',
    foreground: '220 9% 20%',
    card: '240 14% 98%',
    cardForeground: '220 9% 20%',
    muted: '240 10% 90%',
    mutedForeground: '240 5% 45%',
    border: '240 10% 85%',
    input: '240 10% 88%',
    secondary: '255 33% 90%',
    secondaryForeground: '255 33% 30%',
    previewHex: '#F2F2F5',
    isDark: false,
  },
  'gris-oscuro': {
    key: 'gris-oscuro',
    label: 'Negro',
    background: '0 0% 0%',
    foreground: '0 0% 100%',
    card: '0 0% 13%',
    cardForeground: '0 0% 100%',
    muted: '220 10% 25%',
    mutedForeground: '220 10% 60%',
    border: '220 10% 30%',
    input: '220 10% 22%',
    secondary: '220 10% 30%',
    secondaryForeground: '220 10% 90%',
    previewHex: '#000000',
    isDark: true,
  },
  'azul-petroleo': {
    key: 'azul-petroleo',
    label: 'Petróleo',
    background: '195 35% 15%',
    foreground: '195 20% 90%',
    card: '195 35% 22%',
    cardForeground: '195 20% 90%',
    muted: '195 30% 25%',
    mutedForeground: '195 20% 65%',
    border: '195 30% 30%',
    input: '195 30% 25%',
    secondary: '195 30% 35%',
    secondaryForeground: '195 15% 85%',
    previewHex: '#5eafccff',
    isDark: true,
  },
  'verde-corporate': {
    key: 'verde-corporate',
    label: 'Verde Bosque',
    background: '145 45% 20%',
    foreground: '145 15% 92%',
    card: '145 40% 28%',
    cardForeground: '145 15% 92%',
    muted: '145 35% 30%',
    mutedForeground: '145 15% 75%',
    border: '145 35% 35%',
    input: '145 35% 30%',
    secondary: '145 35% 40%',
    secondaryForeground: '145 10% 88%',
    previewHex: '#438a59ff',
    isDark: true,
  },
  'rosa-suave': {
    key: 'rosa-suave',
    label: 'Rosa Pálido',
    background: '340 70% 97%',
    foreground: '340 50% 20%',
    card: '340 80% 94%',
    cardForeground: '340 50% 20%',
    muted: '335 70% 95%',
    mutedForeground: '335 40% 50%',
    border: '335 70% 88%',
    input: '335 70% 92%',
    secondary: '335 70% 90%',
    secondaryForeground: '335 50% 30%',
    previewHex: '#FAE8F5',
    isDark: false,
  },
  'ambar': {
    key: 'ambar',
    label: 'Ámbar Cálido',
    background: '38 95% 90%',
    foreground: '35 70% 25%',
    card: '38 90% 85%',
    cardForeground: '35 70% 25%',
    muted: '35 85% 92%',
    mutedForeground: '35 60% 50%',
    border: '35 85% 78%',
    input: '35 85% 88%',
    secondary: '35 85% 80%',
    secondaryForeground: '35 60% 20%',
    previewHex: '#FAE0B0',
    isDark: false,
  },
  'morado-lavanda': {
    key: 'morado-lavanda',
    label: 'Lavanda',
    background: '260 60% 96%',
    foreground: '260 40% 25%',
    card: '260 70% 92%',
    cardForeground: '260 40% 25%',
    muted: '255 60% 95%',
    mutedForeground: '255 35% 55%',
    border: '255 60% 85%',
    input: '255 60% 90%',
    secondary: '255 60% 88%',
    secondaryForeground: '255 40% 30%',
    previewHex: '#EDE8FE',
    isDark: false,
  },
  'terminal-70s': {
    key: 'terminal-70s',
    label: 'Terminal 70s',
    background: '150 60% 6%',
    foreground: '150 70% 78%',
    card: '150 55% 10%',
    cardForeground: '150 70% 78%',
    muted: '150 40% 12%',
    mutedForeground: '150 50% 50%',
    border: '150 45% 18%',
    input: '150 45% 10%',
    secondary: '150 50% 14%',
    secondaryForeground: '150 60% 70%',
    previewHex: '#1c5f38ff',
    isDark: true,
  },
  'azul-suave': {
    key: 'azul-suave',
    label: 'Azul Cielo',
    background: '205 70% 97%',
    foreground: '205 50% 20%',
    card: '205 80% 93%',
    cardForeground: '205 50% 20%',
    muted: '200 70% 96%',
    mutedForeground: '200 45% 55%',
    border: '200 70% 85%',
    input: '200 70% 90%',
    secondary: '200 70% 88%',
    secondaryForeground: '200 50% 25%',
    previewHex: '#B8D9F0',
    isDark: false,
  },
  'azul-electrico': {
    key: 'azul-electrico',
    label: 'Azul Eléctrico',
    background: '210 40% 10%',
    foreground: '210 20% 95%',
    card: '210 40% 15%',
    cardForeground: '210 20% 95%',
    muted: '210 30% 20%',
    mutedForeground: '210 15% 70%',
    border: '210 30% 25%',
    input: '210 30% 20%',
    secondary: '210 30% 30%',
    secondaryForeground: '210 10% 90%',
    previewHex: '#024d8fff',
    isDark: true,
  },
};

// Orden de display para los selectores (mismo orden en accent y primary)
export const COLOR_ORDER = [
  'default',
  'azul-suave',
  'azul-petroleo',
  'verde-corporate',
  'ambar',
  'rosa-suave',
  'morado-lavanda',
  'gris-oscuro',
  'azul-electrico',
  'terminal-70s',
] as const;

// Helper para obtener accent con fallback
export function getAccentColor(key?: string | null): AccentColor {
  return ACCENT_COLORS[key ?? 'default'] ?? ACCENT_COLORS.default;
}

// Helper para obtener primary color con fallback
export function getPrimaryColor(key?: string | null): PrimaryColor {
  return PRIMARY_COLORS[key ?? 'default'] ?? PRIMARY_COLORS.default;
}

// Color de fondo dominante de cada layout de "look fijo" (ignoran accentColor/primaryColor
// del usuario, ver `fixedLook` en layouts/registry.ts) — se usa tanto para el theme-color de
// la página en vivo (site/[username]/layout.tsx) como para theme_color/background_color del
// manifest de PWA (api/manifest/[username]/route.ts), para que la barra de navegación de
// Android y el ícono/splash de la app instalada combinen con lo que la página realmente
// muestra en vez de con el acento guardado (que estos layouts ignoran para su render). Los
// layouts que SÍ respetan el acento del usuario (todos los demás) NO están aquí — para esos
// se usa ACCENT_COLORS/PRIMARY_COLORS normalmente.
export const FIXED_LAYOUT_THEME_COLOR: Record<string, string> = {
  'crt-80s': '#EDE7DA',
  'halloween-light': '#F0803C',
  'halloween-dark': '#2E1A47',
  'spectral-stripes': '#0a0a0a',
  'gothic-portrait': '#0b0906',
};
